import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authFetch } from "@/lib/api";

const BACKEND_URL = process.env.NODE_ENV === "development"
  ? (process.env.REACT_APP_BACKEND_URL || "")
  : "";

const CurrencyContext = createContext({
  displayCurrency: "USD",
  symbol: "$",
  fxRate: 1,
  rateSource: "live",
  isUSD: true,
  loading: true,
  setDisplayCurrency: () => {},
  fmt: (usdAmount) => `$${usdAmount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
  fmtBoth: (usdAmount) => `$${usdAmount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
});

export function CurrencyProvider({ children }) {
  const [displayCurrency, setDisplayCurrencyState] = useState("USD");
  const [symbol, setSymbol] = useState("$");
  const [fxRate, setFxRate] = useState(1);
  const [rateSource, setRateSource] = useState("live");
  const [loading, setLoading] = useState(true);

  const fetchDisplayCurrency = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/display-currency`);
      if (!res.ok) return;
      const data = await res.json();
      setDisplayCurrencyState(data.display_currency);
      setSymbol(data.symbol);
      setFxRate(data.fx_rate_from_usd);
      setRateSource(data.rate_source);
    } catch {
      // If FX data unavailable, stay on USD
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDisplayCurrency();
  }, [fetchDisplayCurrency]);

  const setDisplayCurrency = useCallback(async (code) => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/display-currency`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_currency: code }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setDisplayCurrencyState(data.display_currency);
      setSymbol(data.symbol);
      setFxRate(data.fx_rate_from_usd);
      setRateSource(data.rate_source);
      return true;
    } catch {
      return false;
    }
  }, []);

  const isUSD = displayCurrency === "USD";

  // Format a USD amount in display currency
  const fmt = useCallback((usdAmount) => {
    if (usdAmount == null || isNaN(usdAmount)) return `${symbol}0`;
    const converted = usdAmount * fxRate;
    return `${symbol}${Math.abs(converted).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }, [symbol, fxRate]);

  // Format showing both: display currency primary, USD secondary (when not USD)
  const fmtBoth = useCallback((usdAmount) => {
    if (usdAmount == null || isNaN(usdAmount)) return `${symbol}0`;
    const usdStr = `$${Math.abs(usdAmount).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    if (isUSD) return usdStr;
    const converted = usdAmount * fxRate;
    return `${symbol}${Math.abs(converted).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }, [symbol, fxRate, isUSD]);

  return (
    <CurrencyContext.Provider value={{
      displayCurrency, symbol, fxRate, rateSource, isUSD, loading,
      setDisplayCurrency, fmt, fmtBoth, fetchDisplayCurrency,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useDisplayCurrency() {
  return useContext(CurrencyContext);
}
